import { resetSessionToken } from '../hooks/usePlaceAutocomplete'

export interface ResolvedPlaceDetails {
  placeId: string
  formattedAddress: string
  latitude: number
  longitude: number
  street: string
  city: string
  state: string
  country: string
  postalCode: string
}

/**
 * Resolves a PlacePrediction to a fully populated ResolvedPlaceDetails object.
 *
 * Calls place.fetchFields() which ends the current autocomplete billing session —
 * the session token is reset after the call so the next autocomplete interaction
 * starts a fresh session.
 *
 * @throws if fetchFields() rejects (e.g. network error, quota exceeded).
 *         Callers should catch and show an appropriate error state.
 */
export async function extractAddressComponents(
  prediction: google.maps.places.PlacePrediction,
): Promise<ResolvedPlaceDetails> {
  const place = prediction.toPlace()

  await place.fetchFields({
    fields: ['id', 'formattedAddress', 'location', 'addressComponents'],
  })

  // Ending the autocomplete session — reset token so the next search is billed separately.
  resetSessionToken()

  const components = place.addressComponents ?? []

  const get = (type: string, useShort = false): string => {
    const match = components.find((c) => c.types.includes(type))
    return match ? (useShort ? match.shortText ?? '' : match.longText ?? '') : ''
  }

  const streetNumber = get('street_number')
  const route = get('route')
  const street = [streetNumber, route].filter(Boolean).join(' ')

  // 'locality' is the standard city component; 'postal_town' is the UK fallback.
  const city = get('locality') || get('postal_town')

  // State/province — use short text (e.g. "BE" not "Berlin", "CA" not "California")
  const state = get('administrative_area_level_1', true)

  const country = get('country')
  const postalCode = get('postal_code')

  return {
    placeId: place.id ?? '',
    formattedAddress: place.formattedAddress ?? '',
    latitude: place.location?.lat() ?? 0,
    longitude: place.location?.lng() ?? 0,
    street,
    city,
    state,
    country,
    postalCode,
  }
}
